import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useMockableViewModel } from '@/hooks'
import { plugSessionsCollection } from '@/reducers/plugSessionCollections'
import { RequestStatus } from '@/request/types'
import { PlugSession, PlugSessions } from '@/types/plugSessions'
import { MOCK_DATA } from './mockData'

// The library Datepicker emits MM/DD/YYYY; the API expects ISO datetimes.
// Normalize here so a picker change can never produce a 400.
const toIsoDate = (date: string) => dayjs(date).format('YYYY-MM-DD')

type Args = {
  /** One of the two must be provided — the charger form maps to the ghost
   *  vehicle server-side via the charger_id param */
  vehicleId?: number
  chargerId?: number
}

type ViewModel = {
  sessions: PlugSessions | null
  isLoading: boolean
  hasError: boolean
  page: number
  onPageChange: (page: number) => void
  startDate: string | null
  endDate: string | null
  onDateRangeChange: (start: string | null, end: string | null) => void
  /** True only when the user picked dates — the displayed range otherwise
   *  comes from the API default (oldest session -> today) */
  isFiltered: boolean
  onResetDateRange: () => void
}

function useViewModel({ vehicleId, chargerId }: Args): ViewModel {
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([
    null,
    null,
  ])
  const [page, setPage] = useState(1)
  // "Load more" appends: the store keeps only the latest envelope, so pages
  // accumulate here; a date-range change resets to page 1
  const [accumulated, setAccumulated] = useState<PlugSession[]>([])

  const params: Record<string, string | number> = {
    ...(vehicleId !== undefined
      ? { vehicle_id: vehicleId }
      : { charger_id: chargerId ?? 0 }),
    page,
  }
  // The BE filters are datetimes — widen the picker's dates to full LOCAL
  // days. UTC-midnight bounds put a 9pm Pacific charge on Sep 30 into an
  // October filter while its card still read "September 30th".
  if (dateRange[0]) {
    params.start_time_after = dayjs(toIsoDate(dateRange[0]))
      .startOf('day')
      .toISOString()
  }
  if (dateRange[1]) {
    params.start_time_before = dayjs(toIsoDate(dateRange[1]))
      .endOf('day')
      .toISOString()
  }

  const { data, status } = plugSessionsCollection.useFetch({ params })
  // Keep showing the previous result while a filter change refetches — the
  // full-section loading bar should only ever appear on first load
  const fetched = data?.[0] ?? null
  const [lastEnvelope, setLastEnvelope] = useState<PlugSessions | null>(null)
  useEffect(() => {
    if (fetched) {
      setLastEnvelope(fetched)
    }
  }, [fetched])
  const envelope = fetched ?? lastEnvelope

  useEffect(() => {
    if (!envelope) {
      return
    }
    setAccumulated((previous) =>
      envelope.page_number > 1
        ? [
            ...previous,
            ...envelope.results.filter(
              (result) => !previous.some((s) => s.id === result.id),
            ),
          ]
        : envelope.results,
    )
  }, [envelope])

  return {
    sessions: envelope ? { ...envelope, results: accumulated } : null,
    isLoading:
      status === RequestStatus.Loading || status === RequestStatus.Idle,
    hasError: status === RequestStatus.Failed,
    page,
    onPageChange: setPage,
    // Until the user picks dates, display the BE's default range:
    // oldest session -> today (the envelope computes it when unfiltered)
    startDate: dateRange[0] ?? envelope?.start_date?.slice(0, 10) ?? null,
    endDate: dateRange[1] ?? envelope?.end_date?.slice(0, 10) ?? null,
    onDateRangeChange: (start, end) => {
      setPage(1)
      setDateRange([start, end])
    },
    isFiltered: dateRange[0] !== null || dateRange[1] !== null,
    onResetDateRange: () => {
      setPage(1)
      setDateRange([null, null])
    },
  }
}

function useMockViewModel(_args: Args): ViewModel {
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([
    '2025-11-08',
    '2026-03-31',
  ])

  return {
    sessions: MOCK_DATA,
    isLoading: false,
    hasError: false,
    page: 1,
    onPageChange: () => {},
    startDate: dateRange[0],
    endDate: dateRange[1],
    onDateRangeChange: (start, end) => setDateRange([start, end]),
    isFiltered: true,
    onResetDateRange: () => setDateRange([null, null]),
  }
}

export default useMockableViewModel({ useViewModel, useMockViewModel })
